import React, { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { ADDR, NET, explorerTx } from '../config';
import { contract, fmt } from '../lib';

const SITE = 'https://agentfleet.fun';
const DOCS = 'https://developers.binance.com/docs/agentic-wallet/quickstart/install-agentic-wallet';
const CHIPS = [1, 3, 5, 10];
const usd = (v) => `$${Number.isInteger(v) ? v : v.toFixed(2)}`;

// How N ships starting at `requested` split across the price tiers (for the "crosses a tier" note).
export function splitByTier(L, n) {
  if (!L || !L.tiers) return [];
  const out = []; let at = L.requested; let left = n;
  for (const t of L.tiers) {
    if (left <= 0) break;
    if (at >= t.end) continue;
    const k = Math.min(left, t.end - at); out.push({ k, usd: t.usd }); at += k; left -= k;
  }
  return out;
}

// The message the user pastes into their agent. Short and chat-style; the full guide lives at /llms.txt.
export function buildPrompt(n, tot, L) {
  const s = n === 1 ? '' : 's';
  const at = new Date(L?.at || Date.now()).toISOString().replace('T', ' ').slice(0, 16);
  const est = tot && tot.usd != null
    ? `Expected total when I copied this (${at} UTC): about ${usd(tot.usd)}${tot.wei != null ? ` = ${fmt(tot.wei, 5)} BNB` : ''}. The price rises by tier, so use the live quote, not this number.`
    : 'The price rises by tier, so always use the live quote.';
  return `Mint ${n} AgentFleet Gen0 ship${s} on ${NET.chainName} (chain id ${NET.chainId}) from my Binance Agentic Wallet (the wallet that holds my Agent ID).
Mint contract (Genesis): ${ADDR.Genesis}
${est}
1. Call canMint(myWallet, ${n}). If it is false, stop and tell me the reason.
2. Call quote(${n}) to get the BNB to send, in wei. Add 2% (the extra is refunded in the same transaction).
3. Simulate mint(${n}) with that value. If it would fail, stop and tell me why.
4. Send one transaction: mint(${n}) to the contract above. Report the tx hash${explorerTx('<hash>') ? ` as ${explorerTx('<hash>')}` : ''}.
5. Do not call settle: the ships are revealed automatically by the random draw (about 10 seconds). Poll mintStatus(pid) every 5 seconds until it is 3, then read MintSettled(pid) -> (firstId, count). My ship ids are firstId..firstId+count-1.
6. Tell me the result in plain words, not raw numbers. For each id read Ships ${ADDR.Ships}: getShip(id) gives kind (0 Miner, 1 Pirate) and tier (0 Common, 1 Rare, 2 Epic, 3 Legendary); hashpowerOf(id) for miners, raidRankOf(id) for pirates; tokenURI(id) is base64 JSON with "name" and "image". Turn the ipfs:// image into https://ipfs.filebase.io/ipfs/... so I can open it. Start with a summary like "You got 3 ships: 2 Rare Miners and 1 Epic Pirate", then one line per ship: id, tier and role, hashpower or raid rank, picture link. If any ship is Legendary, say first: "You pulled a Legendary! Only 20 exist." plus its name. Add the tx link${explorerTx('<hash>') ? ` (${explorerTx('<hash>')})` : ''} and ${SITE}/dashboard, and tell me the next step: open the Fleet page and press "Send to mine" (miners) or "Send to raid" (pirates).
Never approve tokens, sign messages or send BNB to any other address. Max 10 ships per Agent ID.
Full guide: ${SITE}/llms.txt`;
}

// One-screen mint hero: pick quantity -> copy message -> paste into your agent.
export default function AgentMintCard({ L, max = 10, open = true, opensAt }) {
  const [n, setN] = useState(1);
  const [tot, setTot] = useState(null); // { usd, wei }
  const [copied, setCopied] = useState(false);
  const [full, setFull] = useState(false); // message preview collapsed by default (the Copy button always copies the full text)
  const req = L ? L.requested : null;

  useEffect(() => {
    let alive = true; setTot(null);
    if (req == null) return undefined;
    const g = contract('Genesis');
    (async () => {
      const [u, w] = await Promise.all([g.priceUsd(req, n).catch(() => null), g.quote(n).catch(() => null)]);
      if (alive) setTot({ usd: u != null ? Number(ethers.formatEther(u)) : null, wei: w });
    })();
    return () => { alive = false; };
  }, [n, req]);

  const prompt = buildPrompt(n, tot, L);
  const parts = splitByTier(L, n);
  const copy = async () => {
    try { await navigator.clipboard.writeText(prompt); } catch (_) {
      const ta = document.createElement('textarea'); ta.value = prompt; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove();
    }
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };
  const set = (v) => { setN(Math.max(1, Math.min(max, v))); setCopied(false); };

  return (
    <div className="m1-card" data-testid="agent-card">
      <div className="m1-head">
        <h2>🤖 Mint with your Agent ID</h2>
        <p>Only a Binance Agentic Wallet with an Agent ID can mint Gen0. Your agent sends the mint; this page never asks for your wallet.</p>
      </div>

      <div className="m1-step">
        <span className="m1-num">1</span>
        <div className="m1-body">
          <h3>Pick how many</h3>
          <div className="m1-qty">
            <div className="m1-stepper">
              <button aria-label="fewer" onClick={() => set(n - 1)} disabled={n <= 1}>−</button>
              <b data-testid="qty">{n}</b>
              <button aria-label="more" onClick={() => set(n + 1)} disabled={n >= max}>+</button>
            </div>
            <div className="m1-chips">
              {CHIPS.filter((c) => c <= max).map((c) => <button key={c} className={c === n ? 'on' : ''} data-testid={`chip-${c}`} onClick={() => set(c)}>{c}</button>)}
            </div>
            <div className="m1-total" data-testid="total">
              <small>Total now</small>
              <b>{tot && tot.usd != null ? usd(tot.usd) : '…'}</b>
              <span>{tot && tot.wei != null ? `≈ ${fmt(tot.wei, 5)} BNB` : '\u00a0'}</span>
            </div>
          </div>
          <p className="m1-hint">
            One transaction · max {max} per Agent ID
            {parts.length > 1 && <> · crosses a price step: {parts.map((p) => `${p.k} at ${usd(p.usd)}`).join(' + ')}</>}
          </p>
        </div>
      </div>

      <div className="m1-step">
        <span className="m1-num">2</span>
        <div className="m1-body">
          <div className="m1-row">
            <h3>Copy the message for your agent</h3>
            <button className="mint-btn m1-copy" data-testid="agent-copy" onClick={copy}>{copied ? '✓ Copied' : `📋 Copy message · ${n} ship${n === 1 ? '' : 's'}`}</button>
          </div>
          <pre className={`m1-pre${full ? ' m1-pre-full' : ''}`} data-testid="agent-prompt">{prompt}</pre>
          <button type="button" className="m1-more" aria-expanded={full} onClick={() => setFull((f) => !f)}>{full ? 'Hide full message ▴' : 'Show full message ▾'}</button>
        </div>
      </div>

      <div className="m1-step">
        <span className="m1-num">3</span>
        <div className="m1-body">
          <h3>Paste it to your agent</h3>
          <p className="m1-hint">It checks your Agent ID, quotes the live price, dry-runs, then sends one mint. Ships reveal about 10 s later, nothing to sign.</p>
          <p className="m1-hint m1-links">No Agentic Wallet yet? <a href={DOCS} target="_blank" rel="noopener noreferrer">Set one up ↗</a> · <a href="/llms.txt" target="_blank" rel="noopener noreferrer">Full agent guide ↗</a></p>
          {!open && opensAt && <p className="m1-warn">Mint opens {new Date(opensAt * 1000).toLocaleString()}</p>}
        </div>
      </div>
    </div>
  );
}
